if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.slides = Array.from(this.querySelectorAll('.next-gallery__slide'));
        this.thumbs = Array.from(this.querySelectorAll('.next-gallery__thumb'));
        this.dots = Array.from(this.querySelectorAll('.next-gallery__dot'));
        this.slidesContainer = this.querySelector('.next-gallery__slides');
        this.prevBtn = this.querySelector('.next-gallery__nav--prev');
        this.nextBtn = this.querySelector('.next-gallery__nav--next');
        this.expandBtn = this.querySelector('.next-gallery__expand');
        this.thumbUpBtn = this.querySelector('.next-gallery__thumb-nav--up');
        this.thumbDownBtn = this.querySelector('.next-gallery__thumb-nav--down');
        this.mediaCount = this.slides.length;

        // Start at the featured (is-active) slide rather than always index 0
        const activeSlide = this.querySelector('.next-gallery__slide.is-active');
        this.currentIndex = activeSlide ? parseInt(activeSlide.dataset.index) : 0;
        this.currentColor = activeSlide ? activeSlide.dataset.color || '' : '';

        if (this.mediaCount > 0) {
          this.bindEvents();
          this.setupMobileScroll();
          this.setupMobileSwipe();
          // Hide the other colours' images on load so only the selected colour shows.
          this.applyColorFilter(this.currentColor);
          this.updateNavState();
        }
      }

      // Slides/thumbs/dots that aren't hidden by the current colour filter.
      getVisible(list) {
        return list.filter((el) => !el.hasAttribute('data-variant-hidden'));
      }

      // Show only the media belonging to `color` (empty string = show everything,
      // e.g. single-colour products or shared images). Keeps the lightbox in sync.
      applyColorFilter(color) {
        this.currentColor = color || '';
        const hidden = (el) => {
          const c = el.dataset.color || '';
          return !!(this.currentColor && c && c !== this.currentColor);
        };
        this.slides.forEach((el) => el.toggleAttribute('data-variant-hidden', hidden(el)));
        this.thumbs.forEach((el) => el.toggleAttribute('data-variant-hidden', hidden(el)));
        // Dots line up with slides by render order.
        this.dots.forEach((dot, i) =>
          dot.toggleAttribute('data-variant-hidden', this.slides[i] ? this.slides[i].hasAttribute('data-variant-hidden') : false)
        );
        this.syncModalFilter(hidden);
      }

      syncModalFilter(hidden) {
        const modal = document.querySelector(`#ProductModal-${this.dataset.section}`);
        if (!modal) return;
        modal.querySelectorAll('.next-modal__slide, .next-modal__thumb').forEach((el) =>
          el.toggleAttribute('data-variant-hidden', hidden(el))
        );
      }

      // Step to the next/previous VISIBLE slide.
      step(dir) {
        const vis = this.getVisible(this.slides);
        const pos = vis.findIndex((s) => parseInt(s.dataset.index) === this.currentIndex);
        const target = vis[pos + dir];
        if (target) this.goTo(parseInt(target.dataset.index));
      }

      bindEvents() {
        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.goTo(parseInt(thumb.dataset.index)));
        });

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const index = parseInt(dot.dataset.index);
            this.goTo(index);
            if (window.innerWidth < 990 && this.slides[index]) {
              this.slides[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
          });
        });

        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
        if (this.expandBtn) this.expandBtn.addEventListener('click', () => this.openModal(this.currentIndex));

        // Click slide to open modal
        this.slides.forEach((slide) => {
          slide.addEventListener('click', (e) => {
            if (e.target.closest('button')) return;
            this.openModal(parseInt(slide.dataset.index));
          });
        });

        // Thumbnail scroll navigation
        if (this.thumbUpBtn) this.thumbUpBtn.addEventListener('click', () => this.scrollThumbs(-1));
        if (this.thumbDownBtn) this.thumbDownBtn.addEventListener('click', () => this.scrollThumbs(1));

        // Update thumb nav disabled states on thumb list scroll
        const thumbList = this.querySelector('.next-gallery__thumb-list');
        if (thumbList) {
          thumbList.addEventListener('scroll', () => this.updateThumbNavState(), { passive: true });
        }
      }

      setupMobileScroll() {
        if (!this.slidesContainer) return;
        let scrollTimeout;
        this.slidesContainer.addEventListener(
          'scroll',
          () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
              // Carousel is used below the desktop grid breakpoint (<990px).
              if (window.innerWidth >= 990) return;
              const containerRect = this.slidesContainer.getBoundingClientRect();
              const containerCenter = containerRect.left + containerRect.width / 2;
              let closestIndex = this.currentIndex;
              let closestDist = Infinity;
              this.getVisible(this.slides).forEach((slide) => {
                const slideRect = slide.getBoundingClientRect();
                const slideCenter = slideRect.left + slideRect.width / 2;
                const dist = Math.abs(slideCenter - containerCenter);
                if (dist < closestDist) {
                  closestDist = dist;
                  closestIndex = parseInt(slide.dataset.index);
                }
              });
              this.updateActive(closestIndex);
            }, 60);
          },
          { passive: true }
        );
      }

      setupMobileSwipe() {
        if (!this.slidesContainer) return;
        let startX = 0;
        let swiped = false;
        const threshold = 40;

        this.slidesContainer.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          swiped = false;
        }, { passive: true });

        this.slidesContainer.addEventListener('touchmove', (e) => {
          if (swiped) return;
          const dx = e.touches[0].clientX - startX;
          if (Math.abs(dx) > threshold) {
            swiped = true;
            this.goTo(dx < 0 ? this.currentIndex + 1 : this.currentIndex - 1);
          }
        }, { passive: true });
      }

      goTo(index) {
        if (index < 0) index = 0;
        if (index >= this.mediaCount) index = this.mediaCount - 1;
        this.updateActive(index);

        // Below the desktop grid (<990px), scroll the slide into view in the carousel.
        if (window.innerWidth < 990 && this.slides[index]) {
          this.slides[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }

      updateActive(index) {
        this.currentIndex = index;

        this.slides.forEach((slide) => {
          slide.classList.toggle('is-active', parseInt(slide.dataset.index) === index);
        });

        this.thumbs.forEach((thumb) => {
          const isActive = parseInt(thumb.dataset.index) === index;
          thumb.classList.toggle('is-active', isActive);
          if (isActive) {
            thumb.setAttribute('aria-current', 'true');
            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } else {
            thumb.removeAttribute('aria-current');
          }
        });

        this.dots.forEach((dot) => {
          dot.classList.toggle('is-active', parseInt(dot.dataset.index) === index);
        });

        this.updateNavState();
      }

      updateNavState() {
        const vis = this.getVisible(this.slides);
        const pos = vis.findIndex((s) => parseInt(s.dataset.index) === this.currentIndex);
        if (this.prevBtn) this.prevBtn.disabled = pos <= 0;
        if (this.nextBtn) this.nextBtn.disabled = pos >= vis.length - 1;
        this.updateThumbNavState();
      }

      updateThumbNavState() {
        const list = this.querySelector('.next-gallery__thumb-list');
        if (!list || !this.thumbUpBtn || !this.thumbDownBtn) return;
        this.thumbUpBtn.disabled = list.scrollTop <= 2;
        this.thumbDownBtn.disabled = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
      }

      scrollThumbs(direction) {
        const list = this.querySelector('.next-gallery__thumb-list');
        if (list) list.scrollBy({ top: direction * 120, behavior: 'smooth' });
      }

      openModal(index) {
        const sectionId = this.dataset.section;
        const modal = document.querySelector(`#ProductModal-${sectionId}`);
        if (modal && typeof modal.show === 'function') {
          // Pass media ID so the modal can find the correct slide even after reordering
          const slide = this.slides[index];
          const rawMediaId = slide ? slide.dataset.mediaId.replace(`${sectionId}-`, '') : null;
          modal.show(index, rawMediaId);
        }
      }

      // Called by product-info.js when the variant changes. `mediaId` is the
      // selected variant's featured image (one per colour), so its slide's
      // data-color tells us the selected colour.
      setActiveMedia(mediaId) {
        const slide = this.querySelector(`.next-gallery__slide[data-media-id="${mediaId}"]`);
        if (!slide) return;
        const color = slide.dataset.color || '';

        // Same colour (e.g. switching size within one colour): leave the gallery
        // exactly as the shopper left it. Bailing here also avoids the page
        // yanking to the top — the reported "size change scrolls to the top" bug.
        if (color === this.currentColor) return;

        // Colour changed: show only this colour's images and jump to its first one.
        this.applyColorFilter(color);
        this.goTo(parseInt(slide.dataset.index));
        this.updateNavState();

        // On the desktop grid (>=990px), scroll the newly selected colour into view.
        if (window.innerWidth >= 990) {
          const header = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
          const headerOffset = header ? header.offsetHeight : 0;
          const slideTop = slide.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top: slideTop, behavior: 'smooth' });
        }
      }

    }
  );
}
